import React, { Suspense, lazy, useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaUser, FaHeart, FaPlus, FaSearch, FaSignInAlt, FaChevronDown, FaTimes, FaMapMarkedAlt, FaHome } from 'react-icons/fa';
import AddLandCTA from './common/AddLandCTA';
import { clearPhoneAuthToken, setUserPhone, getPhoneAuthToken, getUserPhone } from '../services/auth';
import { logout as logoutAction } from '../store/slices/authSlice';
import { selectCurrentLand } from '../store/slices/landDetailsSlice';
import { useLanguage } from '../contexts/LanguageContext';
import { formatLandType } from './landDetails/LandDetailsOverview';

const SellingLandModal = lazy(() => import('./SellingLandModal'));
const PhoneAuthModal = lazy(() => import('./PhoneAuthModal'));

const Header = ({
  onLogin,
  onPostProperty,
  variant = 'default',
  showBottomNav = true
}) => {
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [landsDropdownOpen, setLandsDropdownOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSellingModal, setShowSellingModal] = useState(false);
  const [isSellingModalClosing, setIsSellingModalClosing] = useState(false);
  // VERSION 1: isSearchActive removed - search is now a separate page
  // const [isSearchActive, setIsSearchActive] = useState(false);
  const [externalModalOpen, setExternalModalOpen] = useState(false);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const landDetails = useSelector(selectCurrentLand);
  // land_type is the field the admin actually edits; property_type can be stale
  const landTypeLabel = formatLandType(landDetails?.landDetails?.landType || landDetails?.type);
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const location = useLocation();

  const handleCloseSellingModal = () => {
    console.log('handleCloseSellingModal called, isSellingModalClosing:', isSellingModalClosing);
    if (isSellingModalClosing) return; // Prevent multiple calls

    setIsSellingModalClosing(true);
    console.log('Set isSellingModalClosing to true');

    // Wait for animation to complete before unmounting
    setTimeout(() => {
      console.log('Unmounting selling modal after animation');
      setShowSellingModal(false);
      setIsSellingModalClosing(false);
    }, 320); // Slightly longer than animation duration to ensure completion
  };

  // VERSION 1: Monitor data-on-search removed - search is now a separate page
  // useEffect(() => {
  //   const checkSearchState = () => {
  //     const onSearch = document.body?.dataset?.onSearch === 'true';
  //     setIsSearchActive(onSearch);
  //   };
  //
  //   checkSearchState();
  //   const observer = new MutationObserver(checkSearchState);
  //   observer.observe(document.body, {
  //     attributes: true,
  //     attributeFilter: ['data-on-search']
  //   });
  //
  //   return () => observer.disconnect();
  // }, []);

  // Compute active bottom tab from current route and menu state
  const activeTab = useMemo(() => {
    // Search page
    if (location.pathname === '/search') return 'search';
    // Account is active when hamburger/menu drawer is open (for logged in users)
    if (mobileMenuOpen && isLoggedIn) return 'account';
    // Likes page
    if (location.pathname.startsWith('/likes')) return 'likes';
    // Home is active when on root path
    if (location.pathname === '/') return 'home';
    return '';
  }, [location.pathname, isLoggedIn, mobileMenuOpen]);

  // Detect external modals (from other pages like MyLandsPage)
  useEffect(() => {
    const checkForModals = () => {
      const hasModal = document.querySelector('.fixed.inset-0.z-\\[100\\]');
      setExternalModalOpen(!!hasModal && !showSellingModal);
    };

    const observer = new MutationObserver(checkForModals);
    observer.observe(document.body, { childList: true, subtree: true });
    checkForModals();

    return () => observer.disconnect();
  }, [showSellingModal]);

  // Monitor reels modal state
  const [isReelsOpen, setIsReelsOpen] = useState(false);
  useEffect(() => {
    const checkReelsState = () => {
      const reelsOpen = document.body?.dataset?.reelsOpen === 'true';
      setIsReelsOpen(reelsOpen);
    };

    checkReelsState();
    const observer = new MutationObserver(checkReelsState);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-reels-open']
    });

    return () => observer.disconnect();
  }, []);

  // No local listener needed; Redux auth slice manages state

  const openLogin = () => setShowSignInModal(true);
  const logout = () => {
    clearPhoneAuthToken();
    try { setUserPhone(''); } catch { }
    try { localStorage.removeItem('phone_number'); localStorage.removeItem('phone_auth_token'); } catch { }
    dispatch(logoutAction());
    setMobileMenuOpen(false);
  };

  // Treat land-details route as details for header styling
  const isDetails = variant === 'details' || variant === 'solid' || location.pathname.startsWith('/land-details');
  const isLikesPage = location.pathname.startsWith('/likes');
  const isMyLandsPage = location.pathname.startsWith('/my-lands');
  const isOwnerLandsPage = variant === 'owner' || location.pathname.startsWith('/owner-lands');
  const isWhiteBackground = isDetails || isLikesPage || isMyLandsPage || isOwnerLandsPage; // solid white on details, likes, my-lands, and owner-lands pages
  // Use green CTA on details, likes, my-lands, and owner-lands pages to match listing style
  const useGreenCTA = isDetails || isLikesPage || isMyLandsPage || isOwnerLandsPage;

  const handleAddLandClick = () => {
    // Redirect to admin portal with auth token
    const token = getPhoneAuthToken();
    const phone = getUserPhone();
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const adminUrl = isLocal ? 'http://localhost:8080' : (process.env.REACT_APP_ADMIN_URL || 'https://llverceladmin.vercel.app');
    
    let redirectUrl = `${adminUrl}/login`;
    if (token && phone) {
      redirectUrl += `#token=${encodeURIComponent(token)}&phone=${encodeURIComponent(phone)}&redirect=/listings/create`;
    } else {
      redirectUrl += `?redirect=/listings/create`;
    }
    window.location.href = redirectUrl;
  };

  return (
    <>
    <header className={`fixed w-full top-0 left-0 right-0 z-50 transition-all duration-300 ${isWhiteBackground ? 'bg-white shadow-sm' : ''}`}>
      <div className="max-w-7xl mx-auto px-3 sm:px-5 lg:px-6">
        <div className={`flex justify-between items-center gap-2 ${isDetails && landDetails ? 'h-auto min-h-[48px] py-2 md:h-14 md:py-0' : 'h-12 md:h-14'}`}>
          {/* Logo or Land Details */}
          <div className="flex-1 min-w-0 md:flex-initial md:flex-shrink-0">
            {/* Show land details on MOBILE ONLY when on land-details page */}
            {isDetails && landDetails && (
              <div className="lg:hidden">
                {/* Line 1: Title only */}
                <div className="flex items-center">
                  <h2 className="text-base md:text-lg font-bold text-brand-blue leading-tight">
                    {landDetails.title || landDetails?.name || landDetails?.layoutName || 'Land Details'}
                  </h2>
                </div>
                {/* Line 2: Location + Chips */}
                <div className="flex items-center gap-1.5 flex-wrap mt-0">
                  {landDetails?.location && (
                    <>
                      <svg className="w-3.5 h-3.5 flex-shrink-0 -ml-0.5" xmlns="http://www.w3.org/2000/svg" width="20.1" height="26.78" viewBox="0 0 20.1 26.78">
                        <path d="M15,1.9A10.089,10.089,0,0,0,4.96,12.02c0,7.93,9.1,16.11,9.49,16.45a.852.852,0,0,0,1.12,0c.39-.34,9.49-8.53,9.49-16.45A10.1,10.1,0,0,0,15.02,1.9h0Zm0,15.62a5.58,5.58,0,1,1,5.58-5.58A5.582,5.582,0,0,1,15,17.52Z" transform="translate(-4.96 -1.9)" fill="#12793c" />
                      </svg>
                      <span className="text-sm text-gray-600">{landDetails.location}</span>
                    </>
                  )}
                  {landTypeLabel && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-50 text-gray-700 border border-gray-200">
                      {landTypeLabel}
                    </span>
                  )}
                  {String(landDetails?.id) === 'dbf1e4bd-b2e0-450f-82f2-7aedca2744f3' && (
                    <span className="relative group inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-brand-blue border border-blue-100">
                      Monitored
                      {/* tooltip */}
                      <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[240px] max-w-[92vw] px-3 py-2 text-xs leading-snug rounded-md shadow-lg bg-white text-gray-900 border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-[60] whitespace-normal">
                        <span className="block font-semibold text-brand-blue mb-0.5">Monitored</span>
                        <span className="block text-gray-700 break-words">This land receives regular aerial updates from LandLayout.com</span>
                        <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rotate-45 bg-white border-l border-t border-gray-200"></span>
                      </span>
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Show logo when: 1) not on land-details page, OR 2) on desktop (lg+) */}
            <Link
              to="/"
              aria-label="Go to homepage"
              className={`inline-block ${isDetails && landDetails ? 'hidden lg:inline-block' : ''}`}
              onClick={(e) => {
                // If we're already on the homepage with no hash, scroll to top smoothly
                if (location.pathname === '/' && !location.hash) {
                  e.preventDefault();
                  try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { }
                }
              }}
            >
              <img
                className={`h-6 sm:h-7 md:h-9 w-auto max-w-[150px] sm:max-w-[200px] md:max-w-none object-contain ${!isWhiteBackground ? 'filter brightness-0 invert' : ''}`}
                src="/LandLayout.com_PNG.png"
                alt="LandLayout Logo"
              />
            </Link>
          </div>

          {/* Centered Navigation */}
          {/*
          <nav className="hidden md:flex items-center space-x-8 justify-center flex-grow font-montserrat">
            <div className="relative group">
              <button
                className="flex items-center space-x-1 text-white group-hover:text-gray-200"
                onClick={() => setLandsDropdownOpen(!landsDropdownOpen)}
              >
                <span>LANDS</span>
                <FaChevronDown className="h-4 w-4 ml-1" />
              </button>
              {landsDropdownOpen && (
                <div className="absolute mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                  Dropdown content
                </div>
              )}
            </div>

            <a href="/career" className="text-white hover:text-gray-200">
              CAREER
            </a>

            <button
              onClick={() => setShowSignInModal(true)}
              className="text-white hover:text-gray-200"
            >
              LOGIN
            </button>
          </nav>
          */}

          {/* Right Section */}
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            {/* Language Toggle - visible on all screens */}
            <div className="flex flex-col items-center mt-2 md:mt-3 flex-shrink-0">
              <button
                onClick={toggleLanguage}
                className="w-10 md:w-14 h-5 md:h-6 rounded-full flex items-center relative cursor-pointer transition-colors bg-white border-2 border-brand-blue"
                aria-label="Toggle language"
              >
                <div
                  className={`absolute w-3 md:w-4 h-3 md:h-4 bg-brand-blue rounded-full transition-transform duration-200 shadow-sm ${language === 'en' ? 'translate-x-0.5' : 'translate-x-[24px] md:translate-x-[33px]'
                    }`}
                />
              </button>
              <div className={`flex gap-1 md:gap-2 text-[10px] md:text-xs font-medium transition-colors ${isReelsOpen ? 'text-white' : 'text-gray-700'}`}>
                <span className="pl-0.5 md:pl-1">En</span>
                <span>ಕನ್ನಡ</span>
              </div>
            </div>

            <div className="hidden lg:flex items-center space-x-4">
              {/* Selling Land Button */}
              {useGreenCTA ? (
                <AddLandCTA variant="green" onClick={handleAddLandClick} />
              ) : (
                <AddLandCTA variant="yellow" onClick={handleAddLandClick} />
              )}
            </div>

            {/* Desktop Search Button (hidden on mobile) - COMMENTED: Now shown at intersection in hero section */}
            {/* <div className="hidden md:flex">
              <button
                onClick={() => navigate('/search')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors text-base leading-none ${!isWhiteBackground
                  ? 'border border-white text-white hover:bg-white hover:text-brand-blue'
                  : 'border border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white'
                  }`}
                aria-label={t('header.search')}
              >
                <FaSearch className="h-4 w-4" />
                <span className="font-normal font-montserrat">{t('header.search')}</span>
              </button>
            </div> */}

            {/* Desktop Login/Profile Icon (hidden on mobile/tablet) */}
            {
              <div className="hidden lg:flex">
                {!isLoggedIn ? (
                  <button
                    onClick={() => setShowSignInModal(true)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors text-base leading-none ${!isWhiteBackground
                      ? 'border border-white text-white hover:bg-white hover:text-brand-blue'
                      : 'border border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white'
                      }`}
                    aria-label={t('header.loginAccount')}
                  >
                    <span className="font-normal font-montserrat">{t('header.loginAccount')}</span>
                  </button>
                ) : (
                  <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className={`p-2 rounded-md ${!isWhiteBackground ? 'text-white' : 'text-gray-700'} hover:bg-gray-100 hover:text-gray-900 transition-colors`}
                    aria-label="Profile menu"
                  >
                    <FaUser className="h-6 w-6" />
                  </button>
                )}
              </div>
            }

          </div>
        </div>
      </div>

      {/* Mobile Menu Drawer (half screen from right) */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileMenuOpen(false)} />
          {/* Panel */}
          <div className="absolute right-0 top-0 h-full w-[280px] max-w-[85vw] sm:w-1/2 sm:min-w-[280px] sm:max-w-xs bg-white shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between px-4 h-12 md:h-14 border-b">
              <img src="/LandLayout.com_PNG.png" alt="LandLayout" className="h-6 w-auto" />
              <button className="p-2" onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
                <FaTimes className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            <div className="divide-y">
              {/* Temporarily hide all options except auth (Login/Logout) in the mobile drawer */}
              {false && (
                <>
                  <button
                    className="w-full flex items-center justify-between px-4 py-4 text-gray-800"
                    onClick={() => setLandsDropdownOpen(!landsDropdownOpen)}
                  >
                    <span>Layout</span>
                    <FaChevronDown className="h-4 w-4" />
                  </button>
                  {landsDropdownOpen && (
                    <div className="px-6 py-2 space-y-2 text-sm text-gray-600">
                      <a href="#plots" className="block">Plots / Sites</a>
                      <a href="#layouts" className="block">Layouts</a>
                      <a href="#farmlands" className="block">Farmlands</a>
                      <a href="#commercial" className="block">Commercial Lands</a>
                    </div>
                  )}

                  <a href="/career" className="block px-4 py-4 text-gray-800">Career</a>
                </>
              )}

              {!isLoggedIn ? (
                <div className="px-4 py-4 flex items-center gap-4">
                  <FaUser className="h-6 w-6 text-gray-700" />
                  <button onClick={() => { openLogin(); setMobileMenuOpen(false); }} className="text-gray-800 border border-gray-200 px-3 py-2 rounded-md">{t('header.loginAccount')}</button>
                </div>
              ) : (
                <>
                  {/* My Lands */}
                  <button className="w-full px-4 py-4 flex items-center gap-3 text-gray-800" onClick={() => { navigate('/my-lands'); setMobileMenuOpen(false); }}>
                    <FaMapMarkedAlt className="h-5 w-5" />
                    <span>My Lands</span>
                  </button>

                  {/* Liked Lands */}
                  <button className="w-full px-4 py-4 flex items-center gap-3 text-gray-800" onClick={() => { navigate('/likes'); setMobileMenuOpen(false); }}>
                    <FaHeart className="h-5 w-5" />
                    <span>Liked Lands</span>
                  </button>

                  <button className="w-full px-4 py-4 flex items-center gap-3 text-gray-800" onClick={logout}>
                    <span>Logout</span>
                  </button>
                </>
              )}

              {/* Optional: Language toggle and Settings temporarily hidden */}
              {false && (
                <>
                  <div className="px-4 py-4 flex items-center justify-between">
                    <span className="text-gray-800">Language</span>
                    <div className="flex items-center">
                      <button
                        onClick={toggleLanguage}
                        className="w-14 h-7 rounded-full bg-blue-500 relative"
                      >
                        <span className={`absolute top-0.5 left-1 h-6 w-6 bg-white rounded-full transition-transform ${language === 'en' ? 'translate-x-0' : 'translate-x-7'}`} />
                      </button>
                      <div className="ml-2 text-xs text-gray-500">En / ಕನ್ನಡ</div>
                    </div>
                  </div>

                  <button className="block px-4 py-4 text-gray-800 text-left">Settings</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation is a SIBLING of <header>, not a child.
          Fixed-inside-fixed + left-1/2 translate glitches on some Android
          WebViews (e.g. Realme 360px) pushing the bar below the viewport.
          This wrapper uses left/right centering with no translate. */}
    </header>

      {/* Bottom Navigation for Mobile & Tablets - visible below lg (1024px) on ALL devices */}
      {showBottomNav && !externalModalOpen && (
        <nav
          className="ll-bottom-navigation block lg:hidden"
          aria-label="Bottom navigation"
        >
          <div className="relative mx-auto w-fit max-w-[calc(100vw-1.5rem)]">
            {/* Bar */}
            <div className="relative bg-white border border-gray-300 rounded-xl overflow-hidden shadow-md flex items-stretch h-11 w-[min(340px,calc(100vw-2rem))] shadow-inner">
              {/* Center lighting effect */}
              <div
                className={`pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 h-full w-24 z-0 ${activeTab ? 'opacity-0' : 'opacity-70'}`}
                aria-hidden="true"
                style={{ background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(209,213,219,0.45) 50%, rgba(255,255,255,0) 100%)' }}
              />
              {/* Left half - Home and Search */}
              <div className="relative z-10 flex flex-1 min-w-0 items-center h-full">
                {/* Home icon */}
                <button
                  className={`flex-1 min-w-0 px-0.5 flex flex-col items-center justify-center h-full ${activeTab === 'home' ? 'bg-brand-green text-white' : 'text-brand-blue'}`}
                  onClick={() => {
                    // Close mobile menu if open
                    if (mobileMenuOpen) setMobileMenuOpen(false);

                    // VERSION 1: Clear search state removed - search is now a separate page

                    // Navigate to home and scroll to reels section
                    if (location.pathname !== '/' || location.hash) {
                      // Coming from another page or have a hash, navigate to clean home
                      navigate('/', { replace: true });
                      // Wait for navigation, then scroll
                      setTimeout(() => {
                        // Look for reels section
                        const reelsSection = document.querySelector('section.pt-6, [class*="ReelsSection"], section [class*="grid"][class*="grid-cols-2"]');
                        if (reelsSection) {
                          const offset = -40; // Offset to show content nicely below header
                          const targetPosition = reelsSection.getBoundingClientRect().top + window.scrollY + offset + (window.innerHeight * 0.1);
                          window.scrollTo({ top: targetPosition, behavior: 'smooth' });
                        } else {
                          window.scrollTo({ top: 100, behavior: 'smooth' });
                        }
                      }, 100);
                    } else {
                      // Already on clean homepage, just scroll to reels
                      const reelsSection = document.querySelector('section.pt-6, [class*="ReelsSection"], section [class*="grid"][class*="grid-cols-2"]');
                      if (reelsSection) {
                        const offset = -40; // Offset to show content nicely below header
                        const targetPosition = reelsSection.getBoundingClientRect().top + window.scrollY + offset + (window.innerHeight * 0.1);
                        window.scrollTo({ top: targetPosition, behavior: 'smooth' });
                      } else {
                        window.scrollTo({ top: 100, behavior: 'smooth' });
                      }
                    }
                  }}
                  aria-label="Home"
                >
                  <FaHome className="h-4 w-4 mb-0.5 shrink-0" />
                  <span className="text-[9px] font-medium leading-tight truncate max-w-full">{t('header.home')}</span>
                </button>
                {/* Saved */}
                <button
                  className={`flex-1 min-w-0 px-0.5 flex flex-col items-center justify-center h-full ${activeTab === 'likes' ? 'bg-brand-green text-white' : 'text-brand-blue'}`}
                  onClick={() => {
                    if (mobileMenuOpen) setMobileMenuOpen(false);

                    // VERSION 1: Clear search state removed - search is now a separate page

                    navigate('/likes');
                  }}
                  aria-label="Favorites"
                >
                  <FaHeart className="h-4 w-4 mb-0.5 shrink-0" />
                  <span className="text-[9px] font-medium leading-tight truncate max-w-full">{t('header.saved')}</span>
                </button>
              </div>

              {/* Center spacer same width as + button (narrower on 320-360px phones) */}
              <div className="w-14 sm:w-16 shrink-0 pointer-events-none" />

              {/* Right half - Search and Profile/Login */}
              <div className="relative z-10 flex flex-1 min-w-0 items-center h-full">
                {/* Search - VERSION 1: Now navigates to dedicated /search page */}
                <button
                  className={`relative z-30 flex-1 min-w-0 px-0.5 flex flex-col items-center justify-center h-full ${location.pathname === '/search' ? 'bg-brand-green text-white' : 'text-brand-blue'}`}
                  onClick={() => {
                    // Close mobile menu if open
                    if (mobileMenuOpen) setMobileMenuOpen(false);

                    // Navigate to search page
                    navigate('/search');
                  }}
                  aria-label="Search"
                >
                  <FaSearch className="h-4 w-4 mb-0.5 shrink-0" />
                  <span className="text-[9px] font-medium leading-tight truncate max-w-full">{t('header.search')}</span>
                </button>
                {/* Profile/Login */}
                {!isLoggedIn ? (
                  <button onClick={() => { openLogin(); }} className="relative z-30 flex-1 min-w-0 px-0.5 flex flex-col items-center justify-center h-full text-brand-blue" aria-label={t('header.loginAccount')}>
                    <FaSignInAlt className="h-4 w-4 mb-0.5 shrink-0" />
                    <span className="text-[9px] font-medium leading-tight truncate max-w-full">{t('header.loginAccount')}</span>
                  </button>
                ) : (
                  <button onClick={() => { setMobileMenuOpen(true); }} className={`relative z-30 flex-1 min-w-0 px-0.5 flex flex-col items-center justify-center h-full ${activeTab === 'account' ? 'bg-brand-green text-white' : 'text-brand-blue'}`} aria-label="Menu">
                    <FaUser className="h-4 w-4 mb-0.5 shrink-0" />
                    <span className="text-[9px] font-medium leading-tight truncate max-w-full">{t('header.account')}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Floating + in Center - transforms to X when modal is open */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[78%] flex flex-col items-center z-[120]">
              <button
                className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white border-4 border-brand-blue flex items-center justify-center shadow-lg transition-transform duration-300"
                onClick={() => {
                  // Close mobile menu if open
                  if (mobileMenuOpen) setMobileMenuOpen(false);
                  // Toggle selling modal
                  showSellingModal ? handleCloseSellingModal() : setShowSellingModal(true);
                }}
                aria-label={showSellingModal ? "Close" : "Add"}
              >
                {showSellingModal ? (
                  <FaTimes className="h-5 w-5 text-brand-blue" />
                ) : (
                  <FaPlus className="h-5 w-5 text-brand-blue" />
                )}
              </button>
              {!showSellingModal && (
                <span className="text-[10px] font-medium leading-tight text-brand-blue mt-0.5 whitespace-nowrap">{t('header.addLand')}</span>
              )}
            </div>
          </div>
        </nav>
      )}


      {/* Selling Land Modal */}
      {(showSellingModal || isSellingModalClosing) && (
        <Suspense fallback={null}>
          <SellingLandModal
            onClose={handleCloseSellingModal}
            isClosing={isSellingModalClosing}
          />
        </Suspense>
      )}

      {/* Phone Auth Modal */}
      {showSignInModal && (
        <Suspense fallback={null}>
          <PhoneAuthModal
            onClose={() => setShowSignInModal(false)}
            onSuccess={() => setShowSignInModal(false)}
            title="Login"
            subtitle="Enter your phone number to continue"
          />
        </Suspense>
      )}

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Search</h2>
              <button
                onClick={() => setShowContactModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            <p className="text-gray-600 mb-4">Search functionality coming soon!</p>
            <button
              onClick={() => setShowContactModal(false)}
              className="w-full bg-brand-green text-white py-2 rounded-lg hover:opacity-90 transition-opacity"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
